import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Divider,
  HStack,
  Typography,
  VStack,
} from '@vivaahealth/design-system';

import Entry, { Row } from '../Entry';

import { PrevisitFormTemplateFieldConfig } from '../../../api';
import * as store from '../../../reducers/store';
import {
  GroupedFields,
  HIDDEN_REVIEWABLE_PRIMITIVES,
  ReviewDataType,
  ReviewSection,
  SectionType,
} from '../../../utils/review';
import { Primitive } from 'vivaa-api/dist/models/previsit/formTemplateFieldConfig';

const { REVIEWABLE_PRIMITIVES, STATIC_PRIMITIVES } =
  PrevisitFormTemplateFieldConfig;

interface ReviewCardProps {
  fields: store.FormFieldWithConfig[];
  sectionType: SectionType;
  onEdit: (sectionType: SectionType) => void;
  sectionConfiguration: ReviewSection;
}

const ReviewCard = ({
  fields,
  sectionType,
  sectionConfiguration,
  onEdit,
}: ReviewCardProps) => {
  const { t } = useTranslation('review');
  const { body, Header } = sectionConfiguration;

  const filteredFields = fields.filter(
    (field) =>
      field.value &&
      REVIEWABLE_PRIMITIVES.includes(field.primitive) &&
      !STATIC_PRIMITIVES.includes(field.primitive) &&
      !HIDDEN_REVIEWABLE_PRIMITIVES.includes(field.primitive),
  );

  const groupedFields = body.reduce(
    (acc: GroupedFields[], groupedReviewData) => {
      const relevantFields = groupedReviewData.data.reduce(
        (acc: store.FormFieldWithConfig[], { type, value }) => {
          const fields = filteredFields.filter((field) =>
            type === ReviewDataType.Primitive
              ? field.primitive === value
              : field.primitive === Primitive.Enum &&
                field.dataBinding === value,
          );

          if (fields.length) {
            acc.push(...fields);
          }

          return acc;
        },
        [],
      );

      if (relevantFields.length) {
        acc.push({
          fields: relevantFields,
          ..._.omit(groupedReviewData, 'data'),
        });
      }

      return acc;
    },
    [],
  );

  if (!groupedFields.length) {
    return null;
  }

  return (
    <Card size="large">
      <Card.Header background="gray">
        <HStack justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="title-large" bold>
            {t(`${_.camelCase(sectionType)}TitleKey`)}
          </Typography>
          <Button
            onClick={() => onEdit(sectionType)}
            size="small"
            variant="text"
          >
            {t('common::edit')}
          </Button>
        </HStack>
      </Card.Header>
      <Card.Body>
        <VStack gap={16}>
          {(!!Header && <Header fields={fields} />) ?? null}
          {groupedFields.map((groupedField, index) => {
            const chunks: store.FormFieldWithConfig[][] = _.chunk(
              groupedField.fields,
              groupedField.columns,
            );

            return (
              <React.Fragment key={JSON.stringify(chunks)}>
                {chunks.map((fields, chunkIndex) => (
                  <Row
                    key={JSON.stringify(fields)}
                    Icon={
                      chunkIndex === 0
                        ? (groupedField.Icon as React.ComponentType<any>)
                        : undefined
                    }
                    hideIconContainer={!!groupedField.hideRow}
                  >
                    {fields.map((field) => (
                      <Entry key={field.id} field={field} />
                    ))}
                  </Row>
                ))}
                {index !== groupedFields.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </VStack>
      </Card.Body>
    </Card>
  );
};

export default ReviewCard;
